import { configureStore, type ThunkAction, type UnknownAction } from '@reduxjs/toolkit'

import { listenForNotifications, persistNotifications, readNotifications } from '@/modules/notifications/storage'
import { notificationsAdapter } from '@/modules/notifications/store/notifications-slice'
import { eventsAdapter } from '@/modules/projects/store'
import { signedIn } from '@/modules/auth/store/auth-slice'
import type { Session } from '@/modules/auth/types'

import { persistHistory, readHistory } from './history-storage'
import { rootReducer, type RootState } from './root-reducer'
import { readEnvelope, SESSION_KEY, SHARED_KEYS, type SharedData } from './shared-state'
import { createSyncContext, type SyncContext } from './sync-context'

/**
 * Reads this tab's session: just the signed-in user's id, kept in browser storage.
 * The id is not checked against the directory here: the directory comes from the backend and loads after the page does.
 * `RequireAuth` waits for it and sends the user to sign in again if they no longer exist.
 */
export function readSession(storage: Storage): Session | null {
  try {
    const userId = storage.getItem(SESSION_KEY)
    if (!userId) return null
    return { userId, signedInAt: new Date().toISOString() }
  } catch {
    return null
  }
}

/** Empty shared data: every slice at its initial state. The app ships no sample data; what it shows comes from the API. */
export function emptySharedData(): SharedData {
  const initial = rootReducer(undefined, { type: '@@init' })
  return Object.fromEntries(SHARED_KEYS.map((key) => [key, initial[key]])) as unknown as SharedData
}

/**
 * Builds a store that starts empty, or from whatever is in shared storage (only tests put anything there). The saved activity
 * history, if any, is loaded into it, and every later change to it is saved.
 */
export function createAppStore(ctx: SyncContext = createSyncContext()) {
  const stored = readEnvelope(ctx.local)
  const shared = stored?.data ?? emptySharedData()
  const history = stored ? [] : readHistory(ctx.history)
  if (history.length) shared.projects = { ...shared.projects, events: eventsAdapter.setAll(shared.projects.events, history) }

  // The notifications saved in this browser come back with it (the newest of each kept), whoever signs in.
  const saved = readNotifications(ctx.history)
  if (saved.length) shared.notifications = notificationsAdapter.upsertMany(shared.notifications, saved)

  const store = configureStore({
    reducer: rootReducer,
    preloadedState: { ...shared, sync: { revision: stored?.revision ?? 0 } } as Partial<RootState>,
    middleware: (getDefault) => getDefault({ thunk: { extraArgument: ctx } }),
  })

  persistHistory(store, ctx.history)
  persistNotifications(store, ctx.history)
  listenForNotifications(store, ctx.history)

  const session = readSession(ctx.session)
  if (session) store.dispatch(signedIn(session))

  return { store, ctx }
}

export type AppStore = ReturnType<typeof createAppStore>['store']
export type AppDispatch = AppStore['dispatch']
export type AppThunk<Result = void> = ThunkAction<Result, RootState, SyncContext, UnknownAction>
